
import React from 'react';
import { SimulationConfig, BallFillType, BallTexture, BallStyle } from '@/types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { Toggle } from './Toggle';
import { ControlGroup } from './ControlGroup';
import { motion } from 'motion/react';

interface SphereDisplaySettingsProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
}

export const SphereDisplaySettings: React.FC<SphereDisplaySettingsProps> = ({ config, setConfig, isLocked }) => {
  const fillTypes: BallFillType[] = ['SOLID', 'GRADIENT_RADIAL', 'GRADIENT_LINEAR', 'HOLLOW'];
  const textures: BallTexture[] = [
    'NONE', 'STRIPES', 'DOTS', 'CROSSHATCH', 'NOISE', 
    'CHECKERBOARD', 'HONEYCOMB', 'CIRCUIT', 'WAVES', 'STARS', 'BRICKS', 'SWIRL'
  ];

  const ballStyleCategories = [
    { name: 'Sleek', styles: ['NONE', 'MATTE', 'GHOST', 'WIREFRAME'] },
    { name: 'Physical', styles: ['SHINY', 'NEON', 'CHROME', 'METAL', 'FROSTED'] },
    { name: 'Visual FX', styles: ['CRYSTAL', 'GLOW', 'HOLOGRAPHIC', 'PLASMA'] }
  ];

  const ColorPicker = ({ label, value, onChange, settingKey }: { label: string, value: string, onChange: (v: string) => void, settingKey?: string }) => (
    <ControlGroup label={label} settingKey={settingKey} darkMode={config.darkMode}>
      <div className={`flex items-center gap-3 p-1.5 rounded-2xl border ${config.darkMode ? 'bg-black/40 border-white/5' : 'bg-white border-neutral-200'} shadow-inner`}>
        <div className="relative group">
          <div className="w-10 h-10 rounded-full border-2 border-white/10 shadow-lg cursor-pointer transition-transform group-hover:scale-105 active:scale-95" style={{ backgroundColor: value }} />
          <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
        </div>
        <input 
          type="text" 
          value={value} 
          onChange={e => onChange(e.target.value)} 
          className={`flex-1 px-3 py-2 text-[11px] font-mono font-bold border rounded-xl outline-none transition-all ${
            config.darkMode 
            ? 'bg-black/20 border-white/5 text-white focus:border-white/20' 
            : 'bg-neutral-50 border-neutral-100 text-neutral-800 focus:border-neutral-300'
          }`} 
        />
      </div>
    </ControlGroup>
  );

  return (
    <div className="flex flex-col gap-6 relative">
      {/* SHAPE CATEGORIES */}
      <div className={`p-4 rounded-3xl border ${config.darkMode ? 'bg-white/5 border-white/10' : 'bg-neutral-50 border-neutral-200'}`}>
        <span className="text-[10px] font-black uppercase opacity-40 mb-4 block">Style Architecture</span>
        <div className="flex flex-col gap-5">
            {ballStyleCategories.map(cat => (
                <ControlGroup key={cat.name} label={`${cat.name} Engine`} settingKey="ballStyle" darkMode={config.darkMode}>
                    <div className={`grid grid-cols-4 gap-1 p-1 w-full rounded-2xl ${config.darkMode ? 'bg-black/40 border border-white/5' : 'bg-white border border-neutral-200'}`}>
                        {cat.styles.map(s => (
                            <button 
                                key={s} 
                                onClick={() => setConfig(prev => ({ ...prev, ballStyle: s as BallStyle }))} 
                                className={`text-[8px] font-black uppercase py-2.5 rounded-xl transition-all relative overflow-hidden ${config.ballStyle === s ? (config.darkMode ? 'text-white bg-white/10' : 'text-neutral-900 shadow-sm bg-zinc-100') : (config.darkMode ? 'text-white/30 hover:text-white/60' : 'text-neutral-400 hover:text-neutral-600')}`}
                            >
                                <span className="relative z-10">{s}</span>
                            </button>
                        ))}
                    </div>
                </ControlGroup>
            ))}
        </div>
      </div>

      {/* GEOMETRY & PHYSICS VIS */}
      <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-blue-500/5 border-blue-500/10' : 'bg-blue-50 border-blue-100'} flex flex-col gap-4 shadow-sm`}>
         <span className="text-[11px] font-black uppercase text-blue-500 opacity-80 mb-2 block">Geometry Dynamics</span>
         <SmartInput label="Entity Radius" min={1} max={150} value={config.ballRadius} onChange={v => setConfig(p => ({...p, ballRadius: v}))} darkMode={config.darkMode} settingKey="ballRadius" />
         
         <div className="flex flex-col gap-4">
             <ControlGroup label="Squash & Stretch" settingKey="ballSquashStretch" darkMode={config.darkMode}>
                <Toggle active={config.ballSquashStretch} onClick={() => setConfig(p => ({...p, ballSquashStretch: !p.ballSquashStretch}))} color="bg-blue-500" />
             </ControlGroup>
             <SmartInput label="Squash Intensity" min={0.1} max={2.0} step={0.1} value={config.ballSquashFactor} onChange={v => setConfig(p => ({...p, ballSquashFactor: v}))} darkMode={config.darkMode} settingKey="ballSquashFactor" />
         </div>
         <SmartInput label="Bio-Pulse Speed" min={0} max={20} value={config.ballPulseSpeed} onChange={v => setConfig(p => ({...p, ballPulseSpeed: v}))} darkMode={config.darkMode} settingKey="ballPulseSpeed" />
      </div>

      {/* FILL & SURFACE */}
      <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-purple-500/5 border-purple-500/10' : 'bg-purple-50 border-purple-100'} flex flex-col gap-4 shadow-sm`}>
         <span className="text-[11px] font-black uppercase text-purple-500 opacity-80 mb-2 block">Material Synthesis</span>
         
         <ControlGroup label="Inner Fill" settingKey="ballFillType" darkMode={config.darkMode}>
            <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                <select value={config.ballFillType} onChange={e => setConfig(p => ({...p, ballFillType: e.target.value as BallFillType}))} className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}>
                    {fillTypes.map(t => <option key={t} value={t} className={config.darkMode ? "bg-neutral-900 text-white" : "bg-white text-neutral-900"}>{t.replace(/_/g, ' ')}</option>)}
                </select>
            </div>
         </ControlGroup>

         <div className="flex flex-col gap-4">
             <ColorPicker label="Base Plasma" value={config.ballColor} onChange={v => setConfig(p => ({...p, ballColor: v}))} settingKey="ballColor" />
             <SmartInput label="Material Opacity" min={0} max={1} step={0.05} value={config.ballFillOpacity} onChange={v => setConfig(p => ({...p, ballFillOpacity: v}))} darkMode={config.darkMode} settingKey="ballFillOpacity" />
         </div>

         {(config.ballFillType === 'GRADIENT_RADIAL' || config.ballFillType === 'GRADIENT_LINEAR') && (
             <div className={`grid grid-cols-1 gap-4 p-4 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-neutral-50 border-neutral-100 shadow-inner'}`}>
                 <ColorPicker label="Gradient Core" value={config.ballGradientInner} onChange={v => setConfig(p => ({...p, ballGradientInner: v}))} settingKey="ballGradientInner" />
                 <ColorPicker label="Gradient Halo" value={config.ballGradientOuter} onChange={v => setConfig(p => ({...p, ballGradientOuter: v}))} settingKey="ballGradientOuter" />
             </div>
         )}

         <ControlGroup label="Texture Overlay" settingKey="ballTexture" darkMode={config.darkMode}>
            <div className={`flex items-center justify-end p-2.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'}`}>
                <select value={config.ballTexture} onChange={e => setConfig(p => ({...p, ballTexture: e.target.value as BallTexture}))} className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}>
                    {textures.map(t => <option key={t} value={t} className={config.darkMode ? "bg-neutral-900 text-white" : "bg-white text-neutral-900"}>{t}</option>)}
                </select>
            </div>
         </ControlGroup>
         {config.ballTexture !== 'NONE' && (
             <SmartInput label="Texture Strength" min={0} max={1} step={0.05} value={config.ballTextureOpacity} onChange={v => setConfig(p => ({...p, ballTextureOpacity: v}))} darkMode={config.darkMode} settingKey="ballTextureOpacity" />
         )}
      </div>

      {/* BORDER & OUTLINE */}
      <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50 border-emerald-100'} flex flex-col gap-4 shadow-sm`}>
         <span className="text-[11px] font-black uppercase text-emerald-500 opacity-80 mb-2 block">Outline Engine</span>
         <div className="flex flex-col gap-4">
             <SmartInput label="Stroke Width" min={0} max={20} value={config.ballStrokeWidth} onChange={v => setConfig(p => ({...p, ballStrokeWidth: v}))} darkMode={config.darkMode} settingKey="ballStrokeWidth" />
             <SmartInput label="Stroke Opacity" min={0} max={1} step={0.05} value={config.ballStrokeOpacity} onChange={v => setConfig(p => ({...p, ballStrokeOpacity: v}))} darkMode={config.darkMode} settingKey="ballStrokeOpacity" />
         </div>
         <ColorPicker label="Stroke Aura" value={config.ballStrokeColor} onChange={v => setConfig(p => ({...p, ballStrokeColor: v}))} settingKey="ballStrokeColor" />
      </div>

      {/* GLOW & FX */}
      <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-orange-500/5 border-orange-500/10' : 'bg-orange-50 border-orange-100'} flex flex-col gap-4 shadow-sm`}>
         <span className="text-[11px] font-black uppercase text-orange-500 opacity-80 mb-2 block">Radiance & Glow</span>
         <div className="flex flex-col gap-4">
             <SmartInput label="Glow Radius" min={0} max={100} value={config.ballGlowRadius} onChange={v => setConfig(p => ({...p, ballGlowRadius: v}))} darkMode={config.darkMode} settingKey="ballGlowRadius" />
             <SmartInput label="Glow Intensity" min={0} max={1} step={0.05} value={config.ballGlowOpacity} onChange={v => setConfig(p => ({...p, ballGlowOpacity: v}))} darkMode={config.darkMode} settingKey="ballGlowOpacity" />
         </div>
         <ColorPicker label="Glow Color" value={config.ballGlowColor} onChange={v => setConfig(p => ({...p, ballGlowColor: v}))} settingKey="ballGlowColor" />
      </div>
      
      {/* MULTI-BALL COLORS override (Legacy compat) */}
      <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-neutral-500/5 border-neutral-500/10' : 'bg-neutral-50 border-neutral-100'} flex flex-col gap-4 shadow-sm`}>
        <span className="text-[11px] font-black uppercase opacity-40 mb-2 block">Entity Overrides</span>
        <div className="grid grid-cols-2 gap-4">
            <ColorPicker label="Primary Sphere" value={config.ball1Color} onChange={v => setConfig(p => ({...p, ball1Color: v}))} />
            <ColorPicker label="Secondary Sphere" value={config.ball2Color} onChange={v => setConfig(p => ({...p, ball2Color: v}))} />
        </div>
      </div>
    </div>
  );
};
